import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faXmark } from '@fortawesome/free-solid-svg-icons';

const DROPDOWN_Z_INDEX = 1300;
const PANEL_MAX_HEIGHT = 280;
const VIEWPORT_PAD = 8;

/**
 * Multi-select roles with search inside the dropdown panel (Find applicants toolbar).
 */
export default function HiringRoleMultiSelect({
    roles = [],
    selectedRoles = [],
    onChange,
    placeholder = 'All roles',
    searchPlaceholder = 'Search roles...',
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [panelStyle, setPanelStyle] = useState(null);
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);

    const selected = useMemo(
        () => (Array.isArray(selectedRoles) ? selectedRoles : []),
        [selectedRoles]
    );

    const filteredRoles = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return roles;
        return roles.filter((role) => role.toLowerCase().includes(q));
    }, [roles, search]);

    const updatePanelPosition = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const gap = 6;
        const minW = Math.max(Math.round(rect.width), 260);

        let left = rect.left;
        if (left + minW > window.innerWidth - VIEWPORT_PAD) {
            left = Math.max(VIEWPORT_PAD, window.innerWidth - minW - VIEWPORT_PAD);
        }

        const spaceBelow = window.innerHeight - rect.bottom - gap - VIEWPORT_PAD;
        const spaceAbove = rect.top - gap - VIEWPORT_PAD;
        const openAbove = spaceBelow < 180 && spaceAbove > spaceBelow;
        const available = openAbove ? spaceAbove : spaceBelow;
        const maxHeight = Math.max(160, Math.min(PANEL_MAX_HEIGHT, available));

        setPanelStyle({
            position: 'fixed',
            left,
            width: minW,
            zIndex: DROPDOWN_Z_INDEX,
            maxHeight,
            ...(openAbove
                ? { bottom: window.innerHeight - rect.top + gap }
                : { top: rect.bottom + gap }),
        });
    }, []);

    useLayoutEffect(() => {
        if (!open) {
            setPanelStyle(null);
            return undefined;
        }
        updatePanelPosition();
        const onScrollOrResize = () => updatePanelPosition();
        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize);
        return () => {
            window.removeEventListener('scroll', onScrollOrResize, true);
            window.removeEventListener('resize', onScrollOrResize);
        };
    }, [open, updatePanelPosition]);

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (e) => {
            const target = e.target;
            if (
                rootRef.current?.contains(target) ||
                panelRef.current?.contains(target)
            ) {
                return;
            }
            setOpen(false);
            setSearch('');
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    const toggleOpen = () => {
        setOpen((prev) => {
            if (prev) setSearch('');
            return !prev;
        });
    };

    const toggleRole = (role) => {
        const next = selected.includes(role)
            ? selected.filter((r) => r !== role)
            : [...selected, role];
        onChange(next);
    };

    const triggerLabel =
        selected.length === 0
            ? placeholder
            : selected.length === 1
              ? selected[0]
              : `${selected.length} roles selected`;

    const dropdownPanel = (
        <div
            ref={panelRef}
            className="hiring-role-multi-select-panel"
            style={panelStyle ?? undefined}
            role="presentation"
            onMouseDown={(e) => e.stopPropagation()}
        >
            <div className="hiring-role-multi-select-search">
                <input
                    type="text"
                    className="hiring-role-multi-select-search-input"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={searchPlaceholder}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Search roles"
                />
                {search.trim() !== '' && (
                    <button
                        type="button"
                        className="hiring-role-multi-select-search-clear"
                        aria-label="Clear role search"
                        onClick={() => setSearch('')}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                )}
            </div>
            <div className="hiring-role-multi-select-options" role="listbox" aria-multiselectable="true">
                {filteredRoles.length === 0 ? (
                    <div className="hiring-role-multi-select-empty">No roles found</div>
                ) : (
                    filteredRoles.map((role) => (
                        <label key={role} className="hiring-role-checkbox-row">
                            <input
                                type="checkbox"
                                checked={selected.includes(role)}
                                onChange={() => toggleRole(role)}
                            />
                            <span>{role}</span>
                        </label>
                    ))
                )}
            </div>
        </div>
    );

    return (
        <div
            className={`hiring-role-multi-select${open ? ' hiring-role-multi-select--open' : ''}`}
            ref={rootRef}
        >
            <button
                ref={triggerRef}
                type="button"
                className={`hiring-role-multi-select-trigger${open ? ' hiring-role-multi-select-trigger--open' : ''}`}
                onClick={toggleOpen}
                aria-haspopup="listbox"
                aria-expanded={open}
                title={selected.length > 1 ? selected.join(', ') : undefined}
            >
                <span
                    className={`hiring-role-multi-select-trigger-label${
                        selected.length === 0 ? ' hiring-role-multi-select-trigger-label--placeholder' : ''
                    }`}
                >
                    {triggerLabel}
                </span>
                <FontAwesomeIcon icon={faChevronDown} className="hiring-role-multi-select-chevron" />
            </button>
            {open &&
                panelStyle &&
                typeof document !== 'undefined' &&
                createPortal(dropdownPanel, document.body)}
        </div>
    );
}
